import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  DragIndicator as DragIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Refresh as RestartIcon,
} from '@mui/icons-material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import ColyseusRoomService, { type ColyseusRoomChanges } from '../../../../services/back4app/ColyseusRoomService'
import MapService, { type EnabledMap } from '../../../../services/back4app/MapService'
import type { ColyseusRoomRecord } from '../../../../../../types/Maps'
import { getServerBaseUrl } from '../../../../utils/serverUrl'
import { getStoredSessionToken } from '../../../../utils/sessionToken'
import { useSurface } from '../../../../hooks/usePermission'

/**
 * A company's Colyseus rooms (SC-422). Platform admins and the company's own admins create,
 * edit, reorder and delete rooms through Cloud Code. Only platform admins see the room controls
 * (start, stop, restart) and can keep a room running when it is empty; company admins' rooms
 * start when someone joins, so their edits apply the next time the room starts (plan D6, D12).
 * The lobby is created with the company: it can't be deleted and stays on a lobby map.
 */
interface CompanyRoomsDialogProps {
  open: boolean
  onClose: () => void
  company: { id: string; name: string } | null
  /** Platform admins get the room controls and the "keep running" switch. */
  isPlatformAdmin?: boolean
}

type SnackbarState = { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }

interface RoomForm {
  name: string
  description: string
  mapId: string
  spawnX: string
  spawnY: string
  maxPlayers: string
  /** A new password; blank keeps the current one. */
  password: string
  removePassword: boolean
  persistent: boolean
}

const chipSx = {
  color: 'var(--sevv-text-secondary, #757575)',
  fontSize: '0.75rem',
  fontWeight: 500,
  backgroundColor: 'var(--sevv-gray-100, #f5f5f5)',
  padding: '0.25rem 0.5rem',
  borderRadius: '0.375rem',
} as const

const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Unknown error')

function roomControlRequest(body: Record<string, unknown>) {
  return fetch(`${getServerBaseUrl()}/api/room-control`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getStoredSessionToken()}` },
    body: JSON.stringify(body),
  }).then((res) => res.json().catch(() => ({ success: false, message: res.statusText })))
}

/** Restarts a company's room for one of its admins (platform admins use room control). */
function companyRoomRestartRequest(customRoomId: string) {
  return fetch(`${getServerBaseUrl()}/api/company-rooms/${encodeURIComponent(customRoomId)}/restart`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getStoredSessionToken()}` },
  }).then((res) => res.json().catch(() => ({ success: false, error: res.statusText })))
}

const SortableRoomItem = ({
  room,
  mapName,
  canControl,
  canRestart,
  onEdit,
  onDelete,
  onRoomControl,
  onRequestRestart,
}: {
  room: ColyseusRoomRecord
  mapName: string
  canControl: boolean
  /** Restarting a room needs rooms.manage (SC-439); the server checks it again. */
  canRestart: boolean
  onEdit: (room: ColyseusRoomRecord) => void
  onDelete: (room: ColyseusRoomRecord) => void
  onRoomControl: (action: 'start' | 'stop' | 'restart', room: ColyseusRoomRecord) => Promise<void>
  /** Asks before restarting: a restart disconnects everyone in the room. */
  onRequestRestart: (room: ColyseusRoomRecord) => void
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: room.customRoomId })
  const [controlLoading, setControlLoading] = useState<string | null>(null)

  const control = async (action: 'start' | 'stop' | 'restart') => {
    if (action === 'restart') {
      onRequestRestart(room)
      return
    }
    setControlLoading(action)
    try {
      await onRoomControl(action, room)
    } finally {
      setControlLoading(null)
    }
  }

  return (
    <Box
      ref={setNodeRef}
      data-testid={`company-room-${room.customRoomId}`}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      sx={{
        padding: '1.25rem',
        backgroundColor: 'var(--sevv-bg-paper, #ffffff)',
        borderRadius: '0.75rem',
        border: '1px solid var(--sevv-divider, #eeeeee)',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        '&:hover': { borderColor: 'var(--sevv-primary, #f97415)' },
      }}
    >
      <Box {...attributes} {...listeners} sx={{ cursor: 'grab', color: 'var(--sevv-text-secondary, #757575)', display: 'flex' }}>
        <DragIcon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', mb: 0.5 }}>
          <Typography sx={{ fontWeight: 600, color: 'var(--sevv-text-primary, #212121)' }}>{room.name}</Typography>
          {room.isLobby && <Chip label="Lobby" size="small" color="primary" variant="outlined" />}
          {room.hasPassword && <Chip label="Password" size="small" variant="outlined" />}
        </Box>
        <Typography variant="body2" sx={{ color: 'var(--sevv-text-secondary, #757575)', mb: 1 }}>
          {room.description || ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={chipSx}>Map: {mapName}</Typography>
          <Typography variant="caption" sx={chipSx}>
            Start: ({room.spawnPoint?.x}, {room.spawnPoint?.y})
          </Typography>
          <Typography variant="caption" sx={{ ...chipSx, fontFamily: 'monospace' }}>ID: {room.customRoomId}</Typography>
          <Typography variant="caption" sx={chipSx}>{room.persistent ? 'Always running' : 'Starts on join'}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        {canControl && (
          <Box sx={{ display: 'flex', gap: '0.25rem', borderRight: '1px solid var(--sevv-divider, #eeeeee)', pr: 1 }}>
            {(['start', 'stop', 'restart'] as const).map((action) => {
              const Icon = action === 'start' ? StartIcon : action === 'stop' ? StopIcon : RestartIcon
              const label = `${action[0].toUpperCase()}${action.slice(1)} Room`
              return (
                <Tooltip key={action} title={label}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={label}
                      disabled={controlLoading !== null}
                      onClick={(e) => {
                        e.stopPropagation()
                        control(action)
                      }}
                    >
                      {controlLoading === action ? <CircularProgress size={16} /> : <Icon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )
            })}
          </Box>
        )}
        {!canControl && canRestart && (
          <Tooltip title="Restart Room">
            <IconButton size="small" aria-label="Restart Room" onClick={() => onRequestRestart(room)}>
              <RestartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" aria-label="Edit room" onClick={() => onEdit(room)}>
          <EditIcon fontSize="small" />
        </IconButton>
        {!room.isLobby && (
          <IconButton size="small" aria-label="Delete room" onClick={() => onDelete(room)} sx={{ color: 'var(--sevv-error, #e74c3c)' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  )
}

const emptyForm = (map: EnabledMap | undefined): RoomForm => ({
  name: '',
  description: '',
  mapId: map?.mapId ?? '',
  spawnX: map ? String(map.playerStart.x) : '',
  spawnY: map ? String(map.playerStart.y) : '',
  maxPlayers: '',
  password: '',
  removePassword: false,
  persistent: false,
})

const formFromRoom = (room: ColyseusRoomRecord): RoomForm => ({
  name: room.name,
  description: room.description || '',
  mapId: room.mapId,
  spawnX: String(room.spawnPoint?.x ?? ''),
  spawnY: String(room.spawnPoint?.y ?? ''),
  maxPlayers: room.maxPlayers ? String(room.maxPlayers) : '',
  password: '',
  removePassword: false,
  persistent: room.persistent,
})

/** The fields a save sends: everything for a new room, only what changed for an existing one. */
export function roomChanges(original: ColyseusRoomRecord | null, form: RoomForm, isPlatformAdmin: boolean): ColyseusRoomChanges {
  const spawnPoint = { x: Number(form.spawnX), y: Number(form.spawnY) }
  const maxPlayers = form.maxPlayers ? Number(form.maxPlayers) : null
  const changes: ColyseusRoomChanges = {}
  if (!original || form.name.trim() !== original.name) changes.name = form.name.trim()
  if (!original || form.description !== (original.description || '')) changes.description = form.description
  if (!original || form.mapId !== original.mapId) changes.mapId = form.mapId
  if (!original || spawnPoint.x !== original.spawnPoint?.x || spawnPoint.y !== original.spawnPoint?.y) {
    changes.spawnPoint = spawnPoint
  }
  if (original ? maxPlayers !== (original.maxPlayers ?? null) : maxPlayers !== null) changes.maxPlayers = maxPlayers
  if (form.password) changes.password = form.password
  else if (original && form.removePassword) changes.password = null
  if (isPlatformAdmin && (!original || form.persistent !== original.persistent)) changes.persistent = form.persistent
  return changes
}

export const CompanyRoomsDialog: React.FC<CompanyRoomsDialogProps> = ({ open, onClose, company, isPlatformAdmin = false }) => {
  const [loading, setLoading] = useState(false)
  const [rooms, setRooms] = useState<ColyseusRoomRecord[]>([])
  const [maps, setMaps] = useState<EnabledMap[]>([])
  const [editing, setEditing] = useState<ColyseusRoomRecord | 'new' | null>(null)
  const [form, setForm] = useState<RoomForm>(emptyForm(undefined))
  const [bulkLoading, setBulkLoading] = useState<string | null>(null)
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' })
  const [restartTarget, setRestartTarget] = useState<ColyseusRoomRecord | null>(null)
  const canRestart = useSurface('rooms-dialog.restart-room')
  const notify = (message: string, severity: SnackbarState['severity']) => setSnackbar({ open: true, message, severity })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const mapName = (mapId: string) => maps.find((m) => m.mapId === mapId)?.name || mapId
  const editingRoom = editing && editing !== 'new' ? editing : null
  // Rule 3: the lobby stays on a lobby map, so every company keeps a reception.
  const pickableMaps = useMemo(() => (editingRoom?.isLobby ? maps.filter((m) => m.isLobbyMap) : maps), [maps, editingRoom])

  const loadRooms = async () => {
    if (!company?.id) return
    setLoading(true)
    try {
      // An admin screen reads fresh: edits made elsewhere (another admin, another tab) must show.
      setRooms(await ColyseusRoomService.list(company.id, { fresh: true }))
    } catch (error) {
      notify(`Failed to load rooms: ${errorText(error)}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open || !company?.id) return
    // Different company, different subject — see the note in CompanyDashboard. [SC-313]
    setRooms([])
    setEditing(null)
    loadRooms()
    MapService.listEnabled({ fresh: true })
      .then((list) => setMaps(list || []))
      .catch((error) => notify(`Failed to load maps: ${errorText(error)}`, 'error'))
  }, [open, company?.id])

  const startCreate = () => {
    setEditing('new')
    setForm(emptyForm(maps[0]))
  }

  const startEdit = (room: ColyseusRoomRecord) => {
    setEditing(room)
    setForm(formFromRoom(room))
  }

  const pickMap = (mapId: string) => {
    const map = maps.find((m) => m.mapId === mapId)
    // A start point on the old map means nothing on the new one.
    setForm({ ...form, mapId, spawnX: map ? String(map.playerStart.x) : form.spawnX, spawnY: map ? String(map.playerStart.y) : form.spawnY })
  }

  const handleSave = async () => {
    if (!company?.id || !editing) return
    setLoading(true)
    try {
      const changes = roomChanges(editingRoom, form, isPlatformAdmin)
      let saved: ColyseusRoomRecord | null = null
      if (!editingRoom) {
        saved = await ColyseusRoomService.create({
          companyId: company.id,
          name: changes.name as string,
          description: changes.description,
          mapId: changes.mapId as string,
          spawnPoint: changes.spawnPoint,
          password: changes.password || undefined,
          maxPlayers: changes.maxPlayers ?? undefined,
          persistent: changes.persistent,
        })
      } else if (Object.keys(changes).length > 0) {
        saved = await ColyseusRoomService.update(editingRoom.customRoomId, changes)
      }

      // Edits never restart a room by themselves: that would disconnect everyone in it.
      if (saved && editingRoom) {
        notify(`"${saved.name}" saved. Restart the room for the changes to take effect.`, 'success')
      } else if (saved) {
        notify(`"${saved.name}" created`, 'success')
      }
      setEditing(null)
      await loadRooms()
    } catch (error) {
      notify(`Failed to save room: ${errorText(error)}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (room: ColyseusRoomRecord) => {
    setLoading(true)
    try {
      await ColyseusRoomService.remove(room.customRoomId)
      notify('Room deleted', 'success')
      await loadRooms()
    } catch (error) {
      notify(`Failed to delete room: ${errorText(error)}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleRoomControl = async (action: 'start' | 'stop' | 'restart', room: ColyseusRoomRecord) => {
    try {
      const result = await roomControlRequest({ action, customRoomId: room.customRoomId })
      notify(result.message || (result.success ? `"${room.name}" ${action}ed` : `Could not ${action} "${room.name}"`), result.success ? 'success' : 'warning')
    } catch {
      notify('Network error: could not reach server', 'error')
    }
  }

  const confirmRestart = async () => {
    const room = restartTarget
    setRestartTarget(null)
    if (!room) return
    try {
      const result = isPlatformAdmin
        ? await roomControlRequest({ action: 'restart', customRoomId: room.customRoomId })
        : await companyRoomRestartRequest(room.customRoomId)
      notify(
        result.success ? `"${room.name}" restarted` : result.error || result.message || `Could not restart "${room.name}"`,
        result.success ? 'success' : 'warning'
      )
    } catch {
      notify('Network error: could not reach server', 'error')
    }
  }

  const handleBulkControl = async (action: 'refreshAll' | 'restartAll' | 'stopAll') => {
    setBulkLoading(action)
    try {
      const result = await roomControlRequest({ action, companyId: company?.id })
      notify(result.message || (result.success ? 'Done' : 'Failed'), result.success ? 'success' : 'warning')
    } catch {
      notify('Network error: could not reach server', 'error')
    } finally {
      setBulkLoading(null)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!company?.id || !over || active.id === over.id) return
    const oldIndex = rooms.findIndex((r) => r.customRoomId === active.id)
    const newIndex = rooms.findIndex((r) => r.customRoomId === over.id)
    const reordered = arrayMove(rooms, oldIndex, newIndex)
    setRooms(reordered)
    try {
      // Writes only priority, so a reorder can't reset a room's map or start point.
      setRooms(await ColyseusRoomService.reorder(company.id, reordered.map((r) => r.customRoomId)))
    } catch (error) {
      notify(`Failed to reorder rooms: ${errorText(error)}`, 'error')
      await loadRooms()
    }
  }

  const canSave = !!form.name.trim() && !!form.mapId && form.spawnX !== '' && form.spawnY !== ''

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '1rem', border: '1px solid var(--sevv-divider, #eeeeee)' } }}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--sevv-divider, #eeeeee)',
          backgroundColor: 'var(--sevv-bg-default, #fafafa)',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--sevv-text-primary, #212121)' }}>
          Rooms — {company?.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {isPlatformAdmin &&
            (
              [
                ['refreshAll', 'Start rooms that should be running', 'Refresh All'],
                ['restartAll', 'Restart all rooms', 'Restart All'],
                ['stopAll', 'Stop all rooms', 'Stop All'],
              ] as const
            ).map(([action, tip, label]) => (
              <Tooltip key={action} title={tip}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={bulkLoading !== null}
                    onClick={() => handleBulkControl(action)}
                    startIcon={bulkLoading === action ? <CircularProgress size={14} /> : action === 'stopAll' ? <StopIcon /> : <RestartIcon />}
                  >
                    {label}
                  </Button>
                </span>
              </Tooltip>
            ))}
          <IconButton aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '1.5rem !important' }}>
        {!isPlatformAdmin && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Rooms start when the first person joins and stop when the last one leaves; the lobby keeps running. Changes to a room take effect after it restarts. Restarting disconnects everyone in it.
          </Alert>
        )}

        {editing && (
          <Box sx={{ mb: 3, p: 2, borderRadius: '0.75rem', border: '1px solid var(--sevv-divider, #eeeeee)', backgroundColor: 'var(--sevv-bg-default, #fafafa)' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {editingRoom ? `Edit ${editingRoom.isLobby ? 'Lobby' : 'Room'}` : 'Create Room'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Name" fullWidth size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="room-map-label">Map</InputLabel>
                  <Select labelId="room-map-label" label="Map" value={form.mapId} onChange={(e) => pickMap(e.target.value as string)}>
                    {pickableMaps.map((m) => (
                      <MenuItem key={m.mapId} value={m.mapId}>
                        {m.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Description" fullWidth size="small" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField label="Start X" type="number" fullWidth size="small" value={form.spawnX} onChange={(e) => setForm({ ...form, spawnX: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField label="Start Y" type="number" fullWidth size="small" value={form.spawnY} onChange={(e) => setForm({ ...form, spawnY: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Max players (optional)" type="number" fullWidth size="small" inputProps={{ min: 1, max: 300 }} helperText="Up to 300" value={form.maxPlayers} onChange={(e) => setForm({ ...form, maxPlayers: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={editingRoom?.hasPassword ? 'New room password (blank keeps it)' : 'Room password (optional)'}
                  type="password"
                  name="roomPassword"
                  id="company-room-password"
                  // A room password, not the admin's login: keep browsers and password managers from filling it.
                  autoComplete="new-password"
                  inputProps={{ 'data-1p-ignore': true, 'data-lpignore': 'true', 'data-bwignore': true }}
                  fullWidth
                  size="small"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {editingRoom?.hasPassword && (
                  <FormControlLabel
                    control={<Switch checked={form.removePassword} onChange={(e) => setForm({ ...form, removePassword: e.target.checked })} />}
                    label="Remove password"
                  />
                )}
                {isPlatformAdmin && !editingRoom?.isLobby && (
                  <FormControlLabel
                    control={<Switch checked={form.persistent} onChange={(e) => setForm({ ...form, persistent: e.target.checked })} />}
                    label="Keep running when empty"
                  />
                )}
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button onClick={() => setEditing(null)}>Cancel</Button>
              <Button variant="contained" disabled={!canSave || loading} onClick={handleSave}>
                Save
              </Button>
            </Box>
          </Box>
        )}

        {loading && rooms.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={rooms.map((r) => r.customRoomId)} strategy={verticalListSortingStrategy}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {rooms.map((room) => (
                  <SortableRoomItem
                    key={room.customRoomId}
                    room={room}
                    mapName={mapName(room.mapId)}
                    canControl={isPlatformAdmin}
                    canRestart={canRestart}
                    onEdit={startEdit}
                    onDelete={handleDelete}
                    onRoomControl={handleRoomControl}
                    onRequestRestart={setRestartTarget}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid var(--sevv-divider, #eeeeee)', px: 3, py: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={startCreate} disabled={maps.length === 0}>
          Add Room
        </Button>
      </DialogActions>

      <Dialog open={!!restartTarget} onClose={() => setRestartTarget(null)}>
        <DialogTitle>Restart room</DialogTitle>
        <DialogContent>
          <Typography data-testid="restart-room-confirm">
            Restarting &quot;{restartTarget?.name}&quot; disconnects everyone in it; they can rejoin right away. Saved changes take effect once it starts again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestartTarget(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={confirmRestart}>
            Restart
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  )
}

export default CompanyRoomsDialog
